import HttpService from "./HttpService";
import Logger from "../Logger/Logger";
import {SERVER_URL} from "../../Config/Parameters";
import {ErrorCode, ThirtyOneError} from "../Error/ThirtyOneError";
import SampleData from "../SampleData/SampleProxyData.json";
import PowerProxy from "../Models/PowerProxy";
import {powerProxyRepository} from "../Repositories/PowerProxyRepository";
import ConnectionStatus from "../Models/ConnectionStatus";
import HttpResult from "../Models/HttpResult";
import {EXPIRED_TOKEN} from "../../Config/Translations";
import {userRepository} from "../Repositories/UserRepository";

const logger = Logger.Create('PowerProxyService');

/**
 * The Power Proxy Service.
 */
export default class PowerProxyService
{
    /**
     * Requests the server for Power Proxies.
     * @param retrieveDummyData Set true to get the data in the Sample Data folder.
     */
    public static async getProxies(retrieveDummyData = false): Promise<boolean>
    {
        if (retrieveDummyData)
        {
            logger.info("Populating the dashboard with dummy data.");
            const sampleDataString: string = JSON.stringify(SampleData);
            const sampleDataObject: PowerProxy[] = JSON.parse(sampleDataString);

            powerProxyRepository.updatePowerProxies(sampleDataObject);
            return true;
        }
        
        const url = `${SERVER_URL}/powerproxy/`;
        
        logger.info(`Requesting the server for all power proxies...`);
        
        let success = false;
        
        await HttpService.tokenlessGet<PowerProxy[]>(url)
            .then((powerProxy): void =>
            {
                powerProxyRepository.updatePowerProxies(powerProxy);
                
                this.getConnectionStatus();
                
                success = true;
            })
            .catch((error: ThirtyOneError) =>
            {
                logger.error(`Request failed with error: ${error.errorCode}`);
                success = false;
            })
        
        return success;
    }

    /**
     * Retrieves the connection status from the server.
     */
    public static async getConnectionStatus(): Promise<void>
    {
        const url = `${SERVER_URL}/powerproxy/ConnectionStatus`;

        logger.info(`Requesting the server all power proxy connection statuses...`);

        await HttpService.tokenlessGet<ConnectionStatus[]>(url)
            .then((connectionStatus): void =>
            {
                powerProxyRepository.updatePowerProxyConnectionStatuses(connectionStatus);
            })
            .catch((error: ThirtyOneError) =>
            {
                logger.error(`Proxy connection status request failed with error: ${error.errorCode}`);
            })
    }

    /**
     * Makes a request to the server to update the proxy name.
     * @param id The proxy id.
     * @param name The new name for this proxy.
     */
    public static async updateProxyName(id: string, name: string): Promise<HttpResult>
    {
        const result: HttpResult = {
            success: true,
            message: 'Proxy name updated.',
        }
        
        const url = `${SERVER_URL}/powerproxy`;

        logger.info(`Requesting the server to change ${id} to ${name}.`);
        
        const body = {
            id,
            name,
        }
        
        await HttpService.put<void>(url, body)
            .catch((error: ThirtyOneError) =>
            {
                result.success = false;

                if (error.errorCode !== ErrorCode.ExpiredToken)
                {
                    logger.error(`Request to change the proxy name failed with error: ${error.errorCode}`);
                    result.message = `Failed change the proxy name to the following error: ${error.message}`;
                    return;
                }

                result.message = EXPIRED_TOKEN;
                userRepository.removeUserDetails();
            })
        
        return result;
    }
}